import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import BookConsultationButton from "@/components/BookConsultationButton";
import { 
  Phone, PhoneCall, Clock, Users, DollarSign, CheckCircle, ArrowRight,
  Zap, Calendar, MessageSquare, Star, Dumbbell, Activity, Heart,
  Trophy, Timer, UserPlus, CreditCard, Ticket
} from "lucide-react";

const AIReceptionistGymPage = () => {
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    document.title = "AI Receptionist for Gyms Dallas | Fitness Center Virtual Receptionist | Gym Answering Service DFW";
  }, []);

  const stats = [
    { value: "24/7", label: "Membership Inquiries", icon: Clock },
    { value: "Tours", label: "Booked Automatically", icon: Users },
    { value: "100%", label: "Calls Answered", icon: PhoneCall },
    { value: "After-Hours", label: "Signup Coverage", icon: UserPlus }
  ];

  const gymServices = [
    { icon: UserPlus, name: "Membership Inquiries", desc: "Pricing, tour scheduling, membership options, promotions" },
    { icon: Calendar, name: "Class Bookings", desc: "Group fitness reservations, instructor schedules, waitlists" },
    { icon: Dumbbell, name: "Personal Training", desc: "PT session scheduling, trainer matching, package inquiries" },
    { icon: Ticket, name: "Guest Passes", desc: "Free trial handling, guest registration, day pass sales" },
    { icon: CreditCard, name: "Billing Questions", desc: "Payment info, freeze requests, cancellation handling" },
    { icon: Trophy, name: "Programs & Events", desc: "Challenge sign-ups, special events, nutrition programs" }
  ];

  const features = [
    { 
      icon: UserPlus, 
      title: "Membership Sales 24/7", 
      desc: "Answer pricing questions, explain membership tiers, book tours, and capture leads even at midnight. Never lose a prospect to voicemail." 
    },
    { 
      icon: Calendar, 
      title: "Class Booking System", 
      desc: "Books members into group fitness classes, manages waitlists, sends reminders, and handles cancellations automatically." 
    },
    { 
      icon: Dumbbell, 
      title: "PT Scheduling", 
      desc: "Matches prospects with trainers, books sessions, manages trainer calendars, and handles package renewals." 
    },
    { 
      icon: MessageSquare, 
      title: "Tour Scheduling", 
      desc: "Books facility tours at optimal times, confirms appointments, and sends prospects pre-tour info." 
    },
    { 
      icon: CreditCard, 
      title: "Billing Support", 
      desc: "Handles payment questions, processes freeze requests, captures cancellation saves, and updates payment info." 
    },
    { 
      icon: Trophy, 
      title: "Program Promotion", 
      desc: "Promotes challenges, special events, and add-on services. Turns routine calls into upsell opportunities." 
    }
  ];

  const comparisons = [
    { item: "Front Desk Staff (FT)", traditional: "$32,000-$42,000/year", ai: "$199-$349/month" },
    { item: "After-Hours Inquiries", traditional: "Lost to competitors", ai: "100% captured 24/7" },
    { item: "Tour No-Shows", traditional: "30-40%", ai: "Reduced by 50%" },
    { item: "Class Booking Time", traditional: "3-5 min/call", ai: "Instant automated" },
    { item: "Membership Questions", traditional: "Hold times vary", ai: "Immediate answers" },
    { item: "Multi-Location Support", traditional: "Staff per location", ai: "One AI, all locations" }
  ];

  const faqs = [
    { 
      q: "How does AI handle gym membership sales calls?", 
      a: "Our AI knows your membership tiers, pricing, promotions, and amenities. It answers questions conversationally, overcomes common objections, and books tours for serious prospects. For after-hours inquiries (which are 40% of calls for gyms), the AI captures leads and schedules tours for the next business day. Members report 35% more tour bookings and 3x more after-hours sign-up inquiries captured."
    },
    { 
      q: "Can AI book group fitness classes for members?",
      a: "Yes. The AI integrates with your gym management software (Mindbody, ClubReady, ABC Fitness, etc.) to see real-time class availability. Members call, the AI confirms their membership, books them into classes, manages waitlists, and sends confirmation texts. It also handles cancellations and rebooking automatically."
    },
    { 
      q: "How does it manage personal training scheduling?",
      a: "The AI sees trainer availability, matches prospects with appropriate trainers based on goals and schedule, books introductory sessions, and handles ongoing PT scheduling. For existing clients, it manages session booking, package renewals, and trainer changes—freeing your trainers to focus on training, not admin."
    },
    { 
      q: "What about billing and membership freeze requests?",
      a: "The AI handles routine billing questions: payment dates, amounts, methods. For freeze requests, it captures the reason, duration, and processes simple freezes or escalates complex situations. For cancellation calls, it's trained to offer retention alternatives (freezes, downgrades, membership transfers) before processing—saving memberships that might otherwise be lost."
    },
    { 
      q: "How does it reduce tour no-shows?",
      a: "The AI sends confirmation texts with facility info, what to bring, and parking details. It also sends reminders 24 hours and 2 hours before tours. If someone needs to reschedule, they can do it via text or call the AI. This systematic approach reduces no-shows by 50% compared to manual scheduling."
    },
    { 
      q: "Does it work with multiple gym locations?",
      a: "Yes. One AI receptionist can handle all your locations. It routes calls to the appropriate location, knows each facility's specific classes and amenities, and can even suggest the most convenient location for prospects based on their address or workplace."
    }
  ];

  const generateSchemas = () => {
    return [
      {
        "@context": "https://schema.org",
        "@type": "Service",
        "name": "AI Receptionist for Gyms & Fitness Centers",
        "description": "24/7 AI-powered virtual receptionist for gyms, fitness studios, and health clubs in Dallas-Fort Worth. Membership sales, class booking, and tour scheduling.",
        "provider": { "@type": "LocalBusiness", "name": "Digital Media Ninja" },
        "areaServed": ["Dallas", "Fort Worth", "Plano", "Arlington", "Frisco", "McKinney"],
        "serviceType": "AI Virtual Receptionist for Fitness Industry"
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": faqs.map(faq => ({
          "@type": "Question",
          "name": faq.q,
          "acceptedAnswer": { "@type": "Answer", "text": faq.a }
        }))
      },
      {
        "@context": "https://schema.org",
        "@type": "Product",
        "name": "AI Gym Receptionist Service",
        "offers": { "@type": "AggregateOffer", "lowPrice": "199", "highPrice": "349", "priceCurrency": "USD" },
      }
    ];
  };

  const schemas = generateSchemas();

  return (
    <div className="min-h-screen bg-black" data-testid="ai-receptionist-gym-page">
      <Helmet>
        <title>AI Receptionist for Gyms Dallas | Fitness Center Virtual Receptionist | 24/7 Gym Answering Service</title>
        <meta name="description" content="AI receptionist for Dallas gyms & fitness centers. 24/7 membership sales, class booking, tour scheduling. Mindbody & ClubReady integration. Capture more members. Free demo!" />
        <meta name="keywords" content="AI receptionist gym Dallas, fitness center virtual receptionist, gym answering service DFW, 24/7 gym phone answering, fitness studio AI, gym membership sales AI" />
        <link rel="canonical" href="https://digitalmedianinja.com/ai-receptionist-gyms" />
        <meta property="og:title" content="AI Receptionist for Gyms Dallas | 24/7 Membership Sales" />
        <meta name="geo.region" content="US-TX" />
        <meta name="geo.placename" content="Dallas" />
        {schemas.map((schema, idx) => (
          <script key={idx} type="application/ld+json">{JSON.stringify(schema)}</script>
        ))}
      </Helmet>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-green-600/20 via-transparent to-green-900/10"></div>
        <div className="container-custom relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-500/10 border border-green-500/30 rounded-full">
                <Dumbbell className="w-4 h-4 text-green-400" />
                <span className="text-green-400 text-sm font-medium">Built for Fitness Industry</span>
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight">
                AI Receptionist for <span className="text-green-400">Gyms & Fitness</span>
              </h1>
              
              <p className="text-xl text-gray-300 max-w-xl">
                Capture every membership lead. AI handles inquiries 24/7, books tours and classes, and turns phone calls into new members—even at midnight.
              </p>
              
              <div className="flex flex-wrap gap-4">
                <BookConsultationButton 
                  meetingType="gym"
                  size="lg"
                  className="bg-green-500 hover:bg-green-600 text-black font-semibold px-8"
                >
                  Schedule Demo <ArrowRight className="w-5 h-5 ml-2" />
                </BookConsultationButton>
                <Link to="/contact">
                  <Button size="lg" variant="outline" className="border-white/20 text-white hover:bg-white/10">
                    <Phone className="w-5 h-5 mr-2" /> Call Us Now
                  </Button>
                </Link>
              </div>
            </div>

            <div className="relative">
              <Card className="bg-white/5 border-green-500/20 backdrop-blur-sm">
                <CardContent className="p-8">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center">
                      <Dumbbell className="w-8 h-8 text-green-400" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white">Fitness Industry AI</h3>
                      <p className="text-green-400">24/7 Membership Sales</p>
                    </div>
                  </div>
                  <div className="space-y-4">
                    {["Mindbody & ClubReady integration", "Class booking & waitlist management", "Tour scheduling with reminders", "Membership tier explanation"].map((item, i) => (
                      <div key={i} className="flex items-center gap-3">
                        <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                        <span className="text-gray-300">{item}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 border-y border-white/10">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, i) => (
              <div key={i} className="text-center">
                <stat.icon className="w-8 h-8 text-green-400 mx-auto mb-3" />
                <div className="text-3xl md:text-4xl font-bold text-white mb-1">{stat.value}</div>
                <div className="text-gray-400">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-20">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Gym Services We Support</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {gymServices.map((service, i) => (
              <Card key={i} className="bg-white/5 border-white/10 hover:border-green-500/50 transition-all">
                <CardContent className="p-6">
                  <service.icon className="w-10 h-10 text-green-400 mb-4" />
                  <h3 className="text-xl font-semibold text-white mb-2">{service.name}</h3>
                  <p className="text-gray-400">{service.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-white/5">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Features for Fitness Businesses</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, i) => (
              <div key={i} className="p-6 rounded-xl bg-black/50 border border-white/10">
                <feature.icon className="w-10 h-10 text-green-400 mb-4" />
                <h3 className="text-xl font-semibold text-white mb-3">{feature.title}</h3>
                <p className="text-gray-400">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Cost Comparison */}
      <section className="py-20">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Gym Reception Cost Comparison</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left py-4 px-6 text-gray-400">Expense</th>
                  <th className="text-center py-4 px-6 text-gray-400">Traditional</th>
                  <th className="text-center py-4 px-6 text-green-400">AI Receptionist</th>
                </tr>
              </thead>
              <tbody>
                {comparisons.map((row, i) => (
                  <tr key={i} className="border-b border-white/10">
                    <td className="py-4 px-6 text-white">{row.item}</td>
                    <td className="py-4 px-6 text-center text-gray-400">{row.traditional}</td>
                    <td className="py-4 px-6 text-center text-green-400 font-semibold">{row.ai}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20">
        <div className="container-custom">
          <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-12">Frequently Asked Questions</h2>
          <div className="max-w-3xl mx-auto space-y-4">
            {faqs.map((faq, i) => (
              <div key={i} className="border border-white/10 rounded-lg overflow-hidden">
                <button className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-white/5" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                  <span className="text-white font-medium">{faq.q}</span>
                  <ArrowRight className={`w-5 h-5 text-green-400 transition-transform ${openFaq === i ? 'rotate-90' : ''}`} />
                </button>
                {openFaq === i && <div className="px-6 pb-4"><p className="text-gray-400">{faq.a}</p></div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Learn More Section */}
      <section className="py-16 border-t border-white/10">
        <div className="container-custom">
          <div className="bg-gradient-to-r from-green-500/10 to-green-600/5 rounded-2xl p-8 md:p-12">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
                  Complete Guide to AI Receptionists for Gyms & Fitness Centers
                </h2>
                <p className="text-gray-300 mb-6">
                  Learn everything about implementing AI reception for your fitness business—from capturing membership leads
                  to integrating with Mindbody and ClubReady, plus real ROI calculations for gyms.
                </p>
                <ul className="space-y-2 mb-6">
                  <li className="flex items-center gap-2 text-gray-300">
                    <CheckCircle className="w-5 h-5 text-green-400" />
                    Gym software integrations guide
                  </li>
                  <li className="flex items-center gap-2 text-gray-300">
                    <CheckCircle className="w-5 h-5 text-green-400" />
                    Lead capture optimization
                  </li>
                  <li className="flex items-center gap-2 text-gray-300">
                    <CheckCircle className="w-5 h-5 text-green-400" />
                    Class booking automation
                  </li>
                </ul>
                <Link to="/blog/ai-receptionist-for-gyms-fitness-centers">
                  <Button className="bg-green-500 hover:bg-green-600 text-black font-semibold">
                    Read the Complete Guide <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
              <div className="hidden md:block">
                <div className="bg-white/5 rounded-xl p-6 border border-green-500/20">
                  <div className="flex items-center gap-3 mb-4">
                    <Dumbbell className="w-8 h-8 text-green-400" />
                    <div>
                      <div className="text-white font-semibold">12 Minute Read</div>
                      <div className="text-gray-400 text-sm">Comprehensive Guide</div>
                    </div>
                  </div>
                  <div className="space-y-3 text-sm text-gray-400">
                    <div>✓ Fitness Business Type Features</div>
                    <div>✓ Tour Scheduling Setup</div>
                    <div>✓ Membership Inquiry Handling</div>
                    <div>✓ FAQ Section with Schema</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-green-600/20 to-green-900/20">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">Ready to Grow Your Membership?</h2>
          <p className="text-gray-300 max-w-2xl mx-auto mb-8">Join Dallas fitness centers using AI to capture every lead and book more members.</p>
          <BookConsultationButton meetingType="aiReceptionist" size="lg" className="bg-green-500 hover:bg-green-600 text-black font-semibold px-8">
            Schedule Your Free Demo
          </BookConsultationButton>
        </div>
      </section>
    </div>
  );
};

export default AIReceptionistGymPage;
